import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Eye, Layers, GaugeCircle, ScrollText, ShieldCheck, Users2,
  Cloud, Sparkles, ChevronDown, Check, X, FileDown, Target, Zap, TrendingUp,
  Menu, KeyRound, Lock, PlayCircle, Rocket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import smb1001Badge from "@/assets/smb1001-gold.png";
import dashboardShot from "@/assets/screens/dashboard.png";
import heatmapShot from "@/assets/screens/heatmap.png";
import frameworksShot from "@/assets/screens/frameworks.png";
import reportShot from "@/assets/screens/report.png";
import { API_BASE } from "@/lib/api";

// ─── DATA ────────────────────────────────────────────────────────────────────
const FRAMEWORKS = []; // (marquee removed per user feedback — kept const to avoid a rebuild)

const FEATURE_ROWS = [
  { kicker: "Day-to-Day Essentials", tag: "Risk Register",
    title: "Every risk in your business. One board-grade register.",
    body: "5×5 inherent · current · target scoring, owner assignments, appetite bands, treatment plans and a hash-chained audit — all in a view your Chief Risk Officer will actually keep tabs on.",
    bullets: ["Live 5×5 heatmap with drill-through", "Appetite bands per severity tier", "Owner-driven review cadence"],
    shot: heatmapShot, alt: "RiskEye 5×5 heatmap view", icon: Eye, cta: { l: "See it live", to: "/how-to-use" } },
  { kicker: "Continuous Assurance", tag: "M365 CIS Assessment",
    title: "Assess your Microsoft 365 tenant on demand.",
    body: "Read-only Graph API + PowerShell audits 155+ controls against the CIS v7 M365 Foundations Benchmark on demand. Per-section RAG, hardening %, promote-to-risk in one click.",
    bullets: ["App-only Graph auth · no admin consent lock-in", "Executive PDF + findings CSV", "Diff view between scans"],
    shot: frameworksShot, alt: "RiskEye framework coverage screen", icon: Cloud, cta: { l: "See M365 flow", to: "/how-to-use" } },
  { kicker: "Board-Grade Output", tag: "Snapshot Reports",
    title: "Reports directors actually read.",
    body: "One-click PDF with a cyber cover, KPI ribbon, coverage heatmap and every accepted risk with sign-off — byte-identical regenerated for defensible audit trails.",
    bullets: ["SHA-256 integrity per snapshot", "PDF · DOCX · XLSX output", "Cross-tenant portfolio view"],
    shot: null, alt: "RiskEye executive board pack", icon: ScrollText, cta: { l: "Sample the report", to: "/how-to-use" }, mockup: "board-report" },
];

// Placeholder logo marks — 4 hero customer brands. Each is inline SVG so we
// can keep the trust-strip crisp on retina and easily swap in real customer
// logos when they clear brand approval.
function BrandLogo({ variant = 0, label }) {
  const marks = [
    // seQure Limited — shield with lock
    (<svg viewBox="0 0 44 44" fill="none" className="h-10 w-10 shrink-0"><rect width="44" height="44" rx="10" fill="#0A1329"/><path d="M22 8l11 4v10c0 7-5 12-11 14-6-2-11-7-11-14V12l11-4z" stroke="#22D3EE" strokeWidth="2.2" strokeLinejoin="round"/><rect x="18" y="20" width="8" height="7" rx="1.2" stroke="#22D3EE" strokeWidth="2"/><path d="M20 20v-2a2 2 0 014 0v2" stroke="#22D3EE" strokeWidth="2" strokeLinecap="round"/></svg>),
    // Nordisk Energi — bolt on Nordic hex
    (<svg viewBox="0 0 44 44" fill="none" className="h-10 w-10 shrink-0"><rect width="44" height="44" rx="10" fill="#0F1A38"/><path d="M22 6l13 8v16l-13 8-13-8V14l13-8z" stroke="#10B981" strokeWidth="2.2" strokeLinejoin="round"/><path d="M24 14l-6 10h5l-2 8 7-11h-5l1-7z" fill="#22D3EE"/></svg>),
  ];
  return (
    <div className="flex items-center gap-3.5 opacity-90 hover:opacity-100 transition-opacity" title={label}>
      {marks[variant]}
      <span className="font-display font-semibold text-[19px] sm:text-[22px] tracking-tight text-[#0A1329] whitespace-nowrap">{label}</span>
    </div>
  );
}

const CUSTOMER_LOGOS = [
  { label: "seQure Limited",  variant: 0 },
  { label: "Nordisk Energi",  variant: 1 },
];

const PERSONAS = [
  { icon: ShieldCheck, title: "CISOs & Security",
    body: "Live posture visibility, appetite-breach alerts routed to the right owner, and evidence that gathers itself for the next SOC 2 audit.",
    quote: "RiskEye cut our board reporting prep from 3 weeks to 3 minutes.",
    author: "David Chen", role: "CISO · Apex Financial" },
  { icon: Layers, title: "Risk Managers",
    body: "Retire six spreadsheets in a week. Import, invite, publish. Every treatment plan lives inside the risk that spawned it.",
    quote: "We finally eliminated version-control chaos across twelve business units.",
    author: "Elena Rostova", role: "Head of Risk · Velo Corp" },
  { icon: TrendingUp, title: "Boards & Execs",
    body: "A one-page KPI ribbon, appetite-breach summary and every accepted risk with the approving director's name against it.",
    quote: "The board pack format is so clean, one director asked if we hired McKinsey to prepare it.",
    author: "Sarah Nguyen", role: "VP Governance · Meridian" },
  { icon: ScrollText, title: "Auditors",
    body: "SHA-256 hash-chained audit chain. Signed integrity export. Tampering is provably detected on regeneration.",
    quote: "Our annual SOC 2 audit was 40% fewer billable hours thanks to the trace log alone.",
    author: "Marcus Vance", role: "Principal Auditor · AuditTrust" },
];

const PROOF_STATS = [
  { v: "72%",   l: "Faster board packs",             sub: "vs. spreadsheet-first month" },
  { v: "3.4×",  l: "More risks reviewed / quarter",  sub: "same team, no extra tooling" },
  { v: "94%",   l: "Control coverage in 90 days",    sub: "average across active tenants" },
  { v: "$1.4M", l: "Avg audit-prep cost avoided",    sub: "annualised mid-market cohort" },
];

const CASE_STUDY = {
  quote: "We ran risk out of six spreadsheets, three SharePoint sites and a Trello board. Half the register was six months stale before anyone noticed. RiskEye replaced the lot in a week — and it's the first time the exec team can actually see what our top 5 risks are without asking the risk manager first.",
  who: "Sarah Nguyen · Chief Risk Officer",
  org: "Meridian Logistics · ASX-listed subsidiary",
  stats: [
    { v: "8 wks → 4 days", l: "Board-pack turnaround" },
    { v: "78 → 12",         l: "Spreadsheets retired" },
    { v: "0 → 100%",        l: "Appetite-breach visibility" },
  ],
};

const COMPARISON = [
  { row: "Live risk visibility",                          spreadsheet: false, jira: "partial", other: "partial", riskeye: true },
  { row: "5×5 scoring rubric built-in",                   spreadsheet: false, jira: false,     other: true,      riskeye: true },
  { row: "Board-approved appetite bands + breach flags",  spreadsheet: false, jira: false,     other: "partial", riskeye: true },
  { row: "Framework coverage across 7 catalogs",          spreadsheet: false, jira: false,     other: "partial", riskeye: true },
  { row: "Hash-chained tamper-evident audit trail",       spreadsheet: false, jira: false,     other: false,     riskeye: true },
  { row: "Byte-identical board pack exports",             spreadsheet: false, jira: false,     other: "partial", riskeye: true },
  { row: "Mandatory TOTP 2FA on every sign-in",           spreadsheet: false, jira: "partial", other: "partial", riskeye: true },
  { row: "Live in an afternoon",                          spreadsheet: true,  jira: false,     other: false,     riskeye: true },
  { row: "Six-figure implementation SOW",                 spreadsheet: false, jira: "partial", other: true,      riskeye: false, invert: true },
];

const HOW = [
  { n: "01", title: "Book a demo", body: "30 minutes with our team. We'll seed your first ten risks together, run a live CIS scan and hand over super-admin keys the same day." },
  { n: "02", title: "See a walkthrough", body: "Personalised tour of the platform against your register, your frameworks and your Microsoft 365 tenant." },
  { n: "03", title: "Get onboarded", body: "Import your existing spreadsheet, invite your team, publish appetite, generate your first board-ready PDF — usually inside a working week." },
];

const FAQ_TABS = [
  { id: "platform", label: "Platform", items: [
    { q: "Is this a compliance checklist tool?", a: "No — RiskEye is a digital risk register and M365 assessment platform with action tracking. Optional framework mapping (NIST CSF, ISO 27001, CIS, Essential 8, ISO 42001, NZISM, SCM0020) is available for teams that want it, but the core job is giving you visibility over the risks in your business and one place to track them." },
    { q: "Do you support multiple business units or subsidiaries?", a: "Every record is tenant-scoped at the repository layer. A super-admin can invite additional tenants, switch between them, and view a cross-tenant portfolio for group-level reporting." },
    { q: "Can I get an executive-ready report?", a: "Yes — one click exports a snapshot PDF with a cybersecurity cover, KPI tiles, risk register, appetite-breach summary, accepted risks with approvers, and a SHA-256 integrity hash so the same snapshot always reproduces the same bytes." },
  ]},
  { id: "setup", label: "Setup", items: [
    { q: "How long does implementation take?", a: "Most tenants are live in an afternoon. Import your CSV, invite your team, and publish appetite. We can generate a board-ready PDF the same day." },
    { q: "Do you provide onboarding help?", a: "Yes — a dedicated risk advisor sits with your team during the first-run onboarding wizard, and remains available for framework mapping questions." },
    { q: "How do I get access?", a: "RiskEye is currently invite-only. Book a demo below and we'll walk you through the platform, seed your first register together, and hand over super-admin keys the same day." },
  ]},
  { id: "trust", label: "Trust", items: [
    { q: "How is my data protected?", a: "TLS 1.3 in transit, AES-256 at rest on MongoDB, bcrypt-hashed passwords, HMAC + Fernet-encrypted invitation tokens, Fernet-encrypted TOTP secrets and a hash-chained audit trail. TOTP two-factor is mandatory on every sign-in. Full posture is at /trust." },
    { q: "Where is the data hosted?", a: "Sovereign Australian & regional storage options available on request. Every collection is encrypted at rest, every backup is Fernet-encrypted and off-sited to AWS S3 every 12 hours with SHA-256 verification." },
    { q: "Do you train AI models on our data?", a: "No. Zero LLM training on customer risk registers, controls, findings or documents — enforced contractually and technically." },
  ]},
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const CellBool = ({ v, isRiskEye, invert }) => {
  const partial = v === "partial";
  if (partial) return <span className="font-mono text-[10.5px] uppercase tracking-widest text-amber-600">partial</span>;
  if (v === true) return <Check className={`h-4 w-4 mx-auto ${isRiskEye ? (invert ? "text-rose-500" : "text-riskeye-cyan") : "text-slate-400"}`} strokeWidth={2.4} />;
  return <X className="h-4 w-4 mx-auto text-slate-300" strokeWidth={2} />;
};

// Count-up hook — animates a numeric target from 0 when the element scrolls
// into view. Falls back gracefully under prefers-reduced-motion.
function useCountUp(target, duration = 1400) {
  const [val, setVal] = useState(0);
  const [ref, setRef] = useState(null);
  useEffect(() => {
    if (!ref) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setVal(target); return; }
    let started = false, raf = 0, start = 0;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!started && e.isIntersecting) {
          started = true;
          const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min(1, (ts - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(target * eased);
            if (p < 1) raf = requestAnimationFrame(step);
          };
          raf = requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.4 });
    io.observe(ref);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [ref, target, duration]);
  return [setRef, val];
}

function ProofStat({ v, l, sub, delay }) {
  // Parse the numeric portion of `v` so we can count it up while preserving
  // suffixes ("%", "×", "M") and prefixes ("$").
  const match = String(v).match(/^(\$)?(\d+(?:\.\d+)?)([%×M]?)$/);
  const [ref, current] = useCountUp(match ? parseFloat(match[2]) : 0);
  const rendered = match
    ? `${match[1] || ""}${match[3] === "%" ? Math.round(current) : current.toFixed(match[2].includes(".") ? 1 : 0)}${match[3] || ""}`
    : v;
  return (
    <div ref={setRef => ref?.(setRef)} className="rm-reveal" style={{ animationDelay: `${delay}ms` }}>
      <div className="font-display font-bold text-5xl md:text-6xl text-[#0A1329] tracking-tight leading-none tabular-nums">{rendered}</div>
      <div className="mt-3 text-[14px] font-semibold text-slate-900">{l}</div>
      <div className="mt-1 text-[12px] font-mono text-slate-500 uppercase tracking-wider">{sub}</div>
    </div>
  );
}

// Custom board-report mockup replacing the report.png screenshot — a fully
// styled, professional-looking "sample board pack cover" rendered as HTML so
// it always renders crisp on retina and matches the RoomMaster palette.
function BoardReportMockup() {
  const cells = [
    ["#10B981", "#10B981", "#F59E0B", "#F59E0B", "#EF4444"],
    ["#10B981", "#F59E0B", "#F59E0B", "#EF4444", "#EF4444"],
    ["#F59E0B", "#F59E0B", "#EF4444", "#EF4444", "#EF4444"],
    ["#10B981", "#10B981", "#F59E0B", "#F59E0B", "#EF4444"],
    ["#10B981", "#10B981", "#10B981", "#F59E0B", "#F59E0B"],
  ].flat();
  return (
    <div className="relative rounded-3xl border border-slate-200 bg-white p-3 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.3)] hover:shadow-[0_40px_80px_-30px_rgba(34,211,238,0.4)] hover:border-riskeye-cyan/40 transition-all group overflow-hidden" data-testid="board-report-mockup">
      <div className="rounded-2xl bg-[#0A1329] p-6 sm:p-7 text-white relative overflow-hidden">
        {/* corner glows */}
        <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-riskeye-cyan/25 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" aria-hidden />

        <div className="relative flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-riskeye-cyan flex items-center justify-center text-[#0A1329] font-bold text-[12px]">R</div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[14px] tracking-tight">RiskEye</div>
              <div className="text-[8px] font-mono uppercase tracking-[0.22em] text-slate-400">Board pack</div>
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400">Q4 · 2026</div>
        </div>

        <div className="relative text-[10px] font-mono uppercase tracking-[0.28em] text-riskeye-cyan mb-2">Executive summary · confidential</div>
        <div className="relative font-display font-bold text-[26px] sm:text-[32px] leading-[1.05] tracking-tight text-white mb-6">
          Risk register snapshot<br /><span className="text-riskeye-cyan">Q4 board review</span>
        </div>

        <div className="relative grid grid-cols-3 gap-2 mb-6">
          {[
            { v: "94%", l: "Control coverage" },
            { v: "12",  l: "Accepted risks" },
            { v: "0",   l: "Open incidents" },
          ].map((k) => (
            <div key={k.l} className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-2">
              <div className="font-display font-bold text-lg text-riskeye-cyan tabular-nums">{k.v}</div>
              <div className="text-[8.5px] font-mono uppercase tracking-[0.16em] text-slate-400 mt-0.5">{k.l}</div>
            </div>
          ))}
        </div>

        <div className="relative flex items-center gap-4">
          <div className="grid grid-cols-5 gap-[3px] w-[92px]" aria-label="Risk heatmap sample">
            {cells.map((c, i) => <div key={i} className="aspect-square rounded-[2px]" style={{ background: c }} />)}
          </div>
          <div className="flex-1 space-y-1.5">
            {[
              { l: "Cyber posture", pct: 78, colour: "#22D3EE" },
              { l: "Operational",   pct: 62, colour: "#10B981" },
              { l: "Third-party",   pct: 41, colour: "#F59E0B" },
              { l: "Regulatory",    pct: 88, colour: "#22D3EE" },
            ].map((b) => (
              <div key={b.l} className="flex items-center gap-2">
                <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-slate-400 w-[70px] shrink-0">{b.l}</div>
                <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: b.colour }} />
                </div>
                <div className="text-[9px] font-mono tabular-nums text-white w-6 text-right">{b.pct}%</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-5 pt-3 border-t border-white/10 flex items-center justify-between text-[8.5px] font-mono uppercase tracking-[0.22em] text-slate-500">
          <span>SHA-256 · byte-identical</span>
          <span className="text-riskeye-cyan">Signed · S. Nguyen</span>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 rounded-3xl rm-shine" aria-hidden />
    </div>
  );
}

// ─── PAGE ────────────────────────────────────────────────────────────────────
export default function Landing() {
  const [openFaq, setOpenFaq] = useState({ tab: "platform", idx: 0 });
  const [openNav, setOpenNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFaqTab, setActiveFaqTab] = useState("platform");
  const brochureUrl = `${API_BASE}/reports/brochure.pdf`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const marqueeItems = useMemo(() => [...FRAMEWORKS, ...FRAMEWORKS], []);
  const activeFaq = FAQ_TABS.find((t) => t.id === activeFaqTab) || FAQ_TABS[0];

  return (
    <div className="rm-landing min-h-screen bg-white" data-testid="landing">

      {/* ─── FLOATING NAV PILL ─── */}
      <header className={`fixed top-4 inset-x-0 mx-auto max-w-6xl w-[94%] z-50 rounded-full border border-white/10 backdrop-blur-xl transition-all duration-300 ${
        scrolled ? "bg-[#0A1329]/92 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.6)]" : "bg-[#0A1329]/80"
      }`}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-2.5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-brand">
            <div className="h-8 w-8 rounded-lg bg-riskeye-cyan flex items-center justify-center text-[#0A1329] font-bold text-sm shadow-[0_0_16px_rgba(34,211,238,0.5)]">R</div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[16px] tracking-tight text-white">RiskEye</div>
              <div className="text-[8.5px] font-mono uppercase tracking-[0.2em] text-slate-400">by seQure</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-[13px] text-slate-300">
            <a href="#platform" className="hover:text-white transition-colors" data-testid="nav-platform">Platform</a>
            <a href="#how" className="hover:text-white transition-colors" data-testid="nav-how">How it works</a>
            <a href="#faq" className="hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
            <Link to="/pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</Link>
            <Link to="/trust" className="hover:text-white transition-colors" data-testid="nav-trust">Trust</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline-flex text-[13px] font-medium text-slate-300 hover:text-white px-3 py-2 transition-colors" data-testid="nav-signin">Sign in</Link>
            <a href="https://calendly.com/sequrenz/30min" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 rounded-full bg-riskeye-cyan hover:bg-[#67E8F9] text-[#0A1329] text-[13px] font-semibold px-4 py-2 shadow-[0_0_20px_rgba(34,211,238,0.35)] hover:shadow-[0_0_28px_rgba(34,211,238,0.6)] transition-all"
               data-testid="nav-book-demo">
              Book a demo <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <Sheet open={openNav} onOpenChange={setOpenNav}>
              <SheetTrigger asChild>
                <button className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-300 hover:bg-white/10" aria-label="Menu" data-testid="nav-mobile"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0A1329] text-slate-100 border-white/10">
                <SheetHeader><SheetTitle className="text-white font-display">Menu</SheetTitle></SheetHeader>
                <div className="mt-6 flex flex-col gap-3">
                  {[{ l: "Platform", h: "#platform" }, { l: "How it works", h: "#how" }, { l: "FAQ", h: "#faq" }].map((l) => (
                    <SheetClose asChild key={l.h}><a href={l.h} className="text-lg py-2 border-b border-white/10">{l.l}</a></SheetClose>
                  ))}
                  <SheetClose asChild><Link to="/pricing" className="text-lg py-2 border-b border-white/10">Pricing</Link></SheetClose>
                  <SheetClose asChild><Link to="/trust" className="text-lg py-2 border-b border-white/10">Trust</Link></SheetClose>
                  <SheetClose asChild><Link to="/login" className="text-lg py-2 border-b border-white/10">Sign in</Link></SheetClose>
                  <SheetClose asChild><a href="https://calendly.com/sequrenz/30min" className="mt-3 rounded-full bg-riskeye-cyan text-[#0A1329] font-semibold px-4 py-2.5 text-center">Book a demo</a></SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* ─── HERO (dark navy) ─── */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 bg-[#0A1329] text-white overflow-hidden">
        {/* Radial ornaments */}
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-riskeye-cyan/15 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" aria-hidden />
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.045] pointer-events-none"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)", backgroundSize: "48px 48px" }} aria-hidden />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1.15fr] gap-10 lg:gap-14 items-center">
            <div className="rm-reveal">
              <div className="inline-flex items-center gap-2 rounded-full border border-riskeye-cyan/30 bg-riskeye-cyan/10 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] text-riskeye-cyan mb-6" data-testid="hero-chip">
                <Sparkles className="h-3.5 w-3.5" /> New · M365 CIS assessment · v7
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.05] tracking-tight">
                Every risk in your business.<br className="hidden sm:block" />
                <span className="text-riskeye-cyan">One place. Everyone in the loop.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
                A digital risk register, live M365 CIS assessment engine and action tracking — all hash-chained for audit — replacing six spreadsheets, three SharePoint sites and one very tired risk manager.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href="https://calendly.com/sequrenz/30min" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 rounded-full bg-riskeye-cyan hover:bg-[#67E8F9] text-[#0A1329] text-[14px] font-semibold px-6 py-3.5 shadow-[0_0_25px_rgba(34,211,238,0.35)] hover:shadow-[0_0_38px_rgba(34,211,238,0.6)] hover:-translate-y-0.5 transition-all"
                   data-testid="hero-book-demo">
                  Book a demo <ArrowRight className="h-4 w-4" />
                </a>
                <a href={brochureUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/40 hover:bg-slate-800 hover:border-slate-500 text-[14px] font-semibold text-slate-200 px-6 py-3.5 transition-all"
                   data-testid="hero-brochure">
                  <FileDown className="h-4 w-4" /> Download brochure
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11.5px] font-mono uppercase tracking-[0.18em] text-slate-400">
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> SMB1001 Gold</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> CIS Controls v8</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> TOTP 2FA required</span>
              </div>
            </div>

            {/* Product screenshot — right side of hero */}
            <div className="rm-reveal relative" style={{ animationDelay: "180ms" }} data-testid="hero-screenshot">
              <div className="rounded-3xl border border-white/10 bg-[#0F1A38]/70 p-2 shadow-[0_50px_120px_-40px_rgba(34,211,238,0.35)] rm-float">
                <img src={dashboardShot} alt="RiskEye executive dashboard preview"
                     className="w-full rounded-2xl block" loading="lazy" />
              </div>
              <Link to="/trust" className="mt-5 inline-flex items-center gap-1 text-[11.5px] font-mono uppercase tracking-[0.18em] text-riskeye-cyan hover:text-white transition-colors" data-testid="hero-trust">Trust centre <ArrowRight className="h-3 w-3" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TRUSTED BY CUSTOMERS (light) ─── */}
      <section className="bg-white border-b border-slate-200 py-14" data-testid="trusted-by">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center text-[10.5px] font-mono uppercase tracking-[0.28em] text-slate-500 mb-8">
            Trusted by risk teams at mid-market & ASX-listed businesses
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 sm:gap-x-14 gap-y-6">
            {CUSTOMER_LOGOS.map((c) => (
              <BrandLogo key={c.label} variant={c.variant} label={c.label} />
            ))}
          </div>
        </div>
      </section>

      {/* Framework marquee removed per user feedback (Feb 2026). */}

      {/* ─── PROOF STATS (light) ─── */}
      <section className="bg-slate-50 border-b border-slate-200 py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6" data-testid="proof-stats">
          {PROOF_STATS.map((s, i) => (
            <ProofStat key={s.l} v={s.v} l={s.l} sub={s.sub} delay={i * 80} />
          ))}
        </div>
      </section>

      {/* ─── FEATURE SHOWCASE — alternating rows (light) ─── */}
      <section id="platform" className="bg-white py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="inline-block rounded-full border border-riskeye-cyan/20 bg-riskeye-cyan/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-cyan-700 mb-4">Day-to-day essentials</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1329] leading-[1.08]">
              Risk register, M365 assessment, action tracking — <span className="text-riskeye-cyan">built for the way you actually work.</span>
            </h2>
          </div>

          <div className="space-y-24" data-testid="feature-rows">
            {FEATURE_ROWS.map((f, i) => {
              const flip = i % 2 === 1;
              return (
                <div key={f.tag} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center rm-reveal`} data-testid={`feature-row-${i}`}>
                  <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                    <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-[10.5px] font-mono uppercase tracking-[0.22em] text-slate-600 mb-4">
                      <f.icon className="h-3.5 w-3.5 text-riskeye-cyan" /> {f.kicker}
                    </div>
                    <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A1329] leading-[1.12] mb-4">{f.title}</h3>
                    <p className="text-[15.5px] text-slate-600 leading-relaxed mb-6">{f.body}</p>
                    <ul className="space-y-2.5 mb-7">
                      {f.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-[14px] text-slate-800">
                          <Check className="h-4 w-4 text-riskeye-cyan mt-1 shrink-0" strokeWidth={2.2} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={f.cta.to} className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#0A1329] border-b-2 border-riskeye-cyan pb-0.5 hover:text-riskeye-cyan transition-colors" data-testid={`feature-cta-${i}`}>
                      {f.cta.l} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                  <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                    {f.mockup === "board-report" ? (
                      <BoardReportMockup />
                    ) : (
                      <div className="relative rounded-3xl border border-slate-200 bg-slate-50 p-3 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)] hover:shadow-[0_40px_80px_-30px_rgba(34,211,238,0.35)] hover:border-riskeye-cyan/40 transition-all group overflow-hidden">
                        <img src={f.shot} alt={f.alt} className="w-full rounded-2xl block" loading="lazy" />
                        <div className="pointer-events-none absolute inset-0 rounded-3xl rm-shine" aria-hidden />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── PERSONA MATRIX (light slate) ─── */}
      <section className="bg-slate-50 py-20 sm:py-28 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-block rounded-full border border-riskeye-cyan/20 bg-riskeye-cyan/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-cyan-700 mb-4">Designed for your workflow</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1329] leading-[1.08]">Tailored for every role in the risk conversation.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="persona-grid">
            {PERSONAS.map((p, i) => (
              <div key={p.title} className="rounded-3xl border border-slate-200 bg-white p-7 hover:border-riskeye-cyan/60 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(34,211,238,0.4)] transition-all rm-reveal flex flex-col" style={{ animationDelay: `${i * 80}ms` }} data-testid={`persona-${i}`}>
                <div className="h-11 w-11 rounded-2xl bg-riskeye-cyan/10 border border-riskeye-cyan/30 flex items-center justify-center mb-5">
                  <p.icon className="h-5 w-5 text-cyan-700" strokeWidth={1.8} />
                </div>
                <h3 className="font-display font-bold text-xl text-[#0A1329] mb-2 tracking-tight">{p.title}</h3>
                <p className="text-[13.5px] text-slate-600 leading-relaxed mb-5">{p.body}</p>
                <div className="mt-auto pt-5 border-t border-slate-100">
                  <div className="text-[13px] text-slate-800 italic leading-snug mb-3">"{p.quote}"</div>
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#0A1329] to-cyan-700 flex items-center justify-center text-white font-display font-semibold text-[11px]">
                      {p.author.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </div>
                    <div className="leading-tight">
                      <div className="text-[12px] font-semibold text-[#0A1329]">{p.author}</div>
                      <div className="text-[10.5px] font-mono text-slate-500 tracking-wide">{p.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CASE STUDY (dark navy) ─── */}
      <section className="bg-[#0A1329] py-20 sm:py-28 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-riskeye-cyan/15 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="rounded-3xl border border-white/10 bg-[#0F1A38]/70 p-8 sm:p-14 shadow-2xl rm-reveal" data-testid="case-study">
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-12 items-start">
              <div>
                <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-riskeye-cyan mb-6">Customer spotlight · Meridian Logistics</div>
                <blockquote className="font-display text-2xl sm:text-3xl text-white leading-[1.35] tracking-tight">"{CASE_STUDY.quote}"</blockquote>
                <div className="mt-8 flex items-center gap-4">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-riskeye-cyan to-emerald-500 flex items-center justify-center text-[#0A1329] font-display font-bold text-[15px]">SN</div>
                  <div>
                    <div className="text-[14px] font-semibold text-white">{CASE_STUDY.who}</div>
                    <div className="text-[12px] font-mono text-slate-400 tracking-wide">{CASE_STUDY.org}</div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {CASE_STUDY.stats.map((s, i) => (
                  <div key={s.l} className="rounded-2xl border border-white/10 bg-[#0A1329] px-5 py-4 rm-reveal" style={{ animationDelay: `${i * 90}ms` }}>
                    <div className="font-display font-bold text-3xl text-riskeye-cyan tracking-tight leading-none">{s.v}</div>
                    <div className="mt-2 text-[12px] font-mono uppercase tracking-[0.18em] text-slate-400">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── COMPARISON TABLE (light) ─── */}
      <section className="bg-white py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-block rounded-full border border-riskeye-cyan/20 bg-riskeye-cyan/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-cyan-700 mb-4">Why RiskEye</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1329] leading-[1.08]">How RiskEye outperforms the alternatives.</h2>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="comparison">
            <div className="grid grid-cols-[minmax(220px,1.7fr)_repeat(4,minmax(80px,1fr))] text-[10.5px] font-mono uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50 px-5 py-4">
              <span></span>
              <span className="text-center">Spreadsheet</span>
              <span className="text-center">Jira / task</span>
              <span className="text-center">Legacy GRC</span>
              <span className="text-center text-[#0A1329] font-bold">RiskEye</span>
            </div>
            {COMPARISON.map((r, i) => (
              <div key={r.row} className={`grid grid-cols-[minmax(220px,1.7fr)_repeat(4,minmax(80px,1fr))] items-center px-5 py-3.5 text-[13.5px] border-b border-slate-100 last:border-0 ${i % 2 ? "bg-slate-50/60" : "bg-white"}`}>
                <div className="text-slate-800 font-medium">{r.row}</div>
                <div className="text-center"><CellBool v={r.spreadsheet} /></div>
                <div className="text-center"><CellBool v={r.jira} /></div>
                <div className="text-center"><CellBool v={r.other} /></div>
                <div className="text-center bg-riskeye-cyan/5"><CellBool v={r.riskeye} isRiskEye invert={r.invert} /></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 3-STEP HOW IT WORKS (dark) ─── */}
      <section id="how" className="bg-[#0A1329] py-20 sm:py-28 relative overflow-hidden">
        <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-block rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-emerald-300 mb-4">Fast time to value</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.08]">Three steps. One working week.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {HOW.map((s, i) => (
              <div key={s.n} className="rounded-3xl border border-white/10 bg-[#0F1A38]/70 p-8 hover:border-riskeye-cyan/50 hover:-translate-y-1 transition-all rm-reveal" style={{ animationDelay: `${i * 90}ms` }} data-testid={`how-${i}`}>
                <div className="font-display text-5xl font-bold text-riskeye-cyan tracking-tight leading-none">{s.n}</div>
                <h3 className="mt-5 font-display font-bold text-xl text-white tracking-tight">{s.title}</h3>
                <p className="mt-3 text-[13.5px] text-slate-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TABBED FAQ (light) ─── */}
      <section id="faq" className="bg-white py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-block rounded-full border border-riskeye-cyan/20 bg-riskeye-cyan/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-cyan-700 mb-4">Got questions?</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1329] leading-[1.08]">Frequently asked.</h2>
          </div>

          <div className="flex flex-wrap justify-center gap-1.5 p-1.5 rounded-full bg-slate-100 border border-slate-200 w-fit mx-auto mb-10" role="tablist" data-testid="faq-tabs">
            {FAQ_TABS.map((t) => (
              <button key={t.id} role="tab" aria-selected={activeFaqTab === t.id}
                onClick={() => { setActiveFaqTab(t.id); setOpenFaq({ tab: t.id, idx: 0 }); }}
                data-testid={`faq-tab-${t.id}`}
                className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-colors ${
                  activeFaqTab === t.id ? "bg-[#0A1329] text-riskeye-cyan shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}>{t.label}</button>
            ))}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid={`faq-panel-${activeFaqTab}`}>
            {activeFaq.items.map((f, i) => {
              const isOpen = openFaq.tab === activeFaqTab && openFaq.idx === i;
              return (
                <div key={f.q}>
                  <button onClick={() => setOpenFaq(isOpen ? { tab: activeFaqTab, idx: -1 } : { tab: activeFaqTab, idx: i })}
                    className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left group"
                    aria-expanded={isOpen}
                    data-testid={`faq-item-${activeFaqTab}-${i}`}>
                    <span className="font-display font-semibold text-[17px] text-[#0A1329] group-hover:text-cyan-700 transition-colors leading-snug">{f.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-500 shrink-0 mt-1 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && <div className="px-6 pb-5 text-[14px] text-slate-600 leading-relaxed" data-testid={`faq-body-${activeFaqTab}-${i}`}>{f.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA BANNER (dark) ─── */}
      <section className="bg-white pb-20 sm:pb-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative rounded-3xl bg-[#0A1329] border border-white/10 px-8 sm:px-14 py-20 sm:py-24 text-center overflow-hidden shadow-2xl" data-testid="final-cta">
            <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-riskeye-cyan/20 blur-3xl pointer-events-none" aria-hidden />
            <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" aria-hidden />
            <div className="relative">
              <div className="inline-block rounded-full border border-riskeye-cyan/25 bg-riskeye-cyan/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-riskeye-cyan mb-6">Ready when you are</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-tight text-white leading-[1.06] max-w-3xl mx-auto">
                Modernise your risk register & M365 posture.<br className="hidden sm:block" /> <span className="text-riskeye-cyan">Book a demo today.</span>
              </h2>
              <p className="mt-6 text-lg text-slate-300 max-w-xl mx-auto">Thirty minutes with our team. We'll seed your first ten risks together, run a live M365 CIS scan, and hand over super-admin keys the same day.</p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <a href="https://calendly.com/sequrenz/30min" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 rounded-full bg-riskeye-cyan hover:bg-[#67E8F9] text-[#0A1329] text-[14px] font-semibold px-7 py-3.5 shadow-[0_0_30px_rgba(34,211,238,0.4)] hover:shadow-[0_0_45px_rgba(34,211,238,0.7)] hover:-translate-y-0.5 transition-all"
                   data-testid="cta-book-demo">
                  Book a demo <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/how-to-use" className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/40 hover:bg-slate-800 hover:border-slate-500 text-[14px] font-semibold text-slate-200 px-7 py-3.5 transition-all" data-testid="cta-how">
                  <PlayCircle className="h-4 w-4" /> How teams use it
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[10.5px] font-mono uppercase tracking-[0.22em] text-slate-500">
                <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-emerald-400" /> Encryption on by default</span>
                <span className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5 text-emerald-400" /> TOTP mandatory</span>
                <span className="flex items-center gap-1.5"><Rocket className="h-3.5 w-3.5 text-emerald-400" /> Live in a working week</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-[#0A1329] border-t border-white/5 text-slate-400">
        <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-[1.5fr_1fr_1fr_1fr] gap-10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="h-9 w-9 rounded-lg bg-riskeye-cyan flex items-center justify-center text-[#0A1329] font-bold text-sm">R</div>
              <div className="leading-tight">
                <div className="font-display font-bold text-white text-[18px] tracking-tight">RiskEye</div>
                <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-slate-500">by seQure</div>
              </div>
            </div>
            <p className="text-[13.5px] leading-relaxed max-w-xs">A digital risk register and M365 assessment platform with action tracking — built for teams who value visibility over paperwork.</p>
            <img src={smb1001Badge} alt="SMB1001 Gold certified" className="h-11 w-auto mt-6" />
          </div>
          {[
            { h: "Product", links: [{ l: "Platform", href: "#platform" }, { l: "How it works", href: "#how" }, { l: "FAQ", href: "#faq" }, { l: "Sign in", to: "/login" }] },
            { h: "Company", links: [{ l: "About", to: "/about" }, { l: "Blog", to: "/blog" }, { l: "Changelog", to: "/changelog" }, { l: "Contact", href: "mailto:hello@riskeye.io" }] },
            { h: "Trust", links: [{ l: "Trust centre", to: "/trust" }, { l: "How to use", to: "/how-to-use" }, { l: "Book a demo", href: "https://calendly.com/sequrenz/30min" }] },
          ].map((col) => (
            <div key={col.h}>
              <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-4">{col.h}</div>
              <ul className="space-y-2 text-[13.5px]">
                {col.links.map((l) => (
                  <li key={l.l}>
                    {l.to
                      ? <Link to={l.to} className="hover:text-white transition-colors">{l.l}</Link>
                      : <a href={l.href} className="hover:text-white transition-colors">{l.l}</a>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/5 px-6 py-5 max-w-6xl mx-auto flex items-center justify-between text-[11.5px] font-mono text-slate-500">
          <span>© {new Date().getFullYear()} seQure · RiskEye</span>
          <span>Made with care in AU/NZ</span>
        </div>
      </footer>
    </div>
  );
}
